<div class="page-layout">
  <app-sidebar></app-sidebar>

  <div class="main-area">
    <app-navbar
      pageTitle="Graph Playground"
      pageSubtitle="Explore the Voyager Demo knowledge graph and ask it questions."
    ></app-navbar>

    <div class="content">
      <div class="graph-card">
        <div class="card-header">
          <div class="header-left">
            <span class="scope-badge">{{ nodeCount() }} nodes · {{ edgeCount() }} edges</span>
          </div>
          <button type="button" class="toolbar-btn" (click)="resetView()">Reset View</button>
        </div>

        <div class="playground-body">
          <div class="graph-stage">
            <div class="loading-overlay" *ngIf="isLoading()">
              <div class="spinner"></div>
            </div>

            <div class="empty-overlay" *ngIf="!isLoading() && loadError()">
              <app-empty-state title="Couldn't load the graph" [message]="loadError()!"></app-empty-state>
            </div>

            <div #graphCanvas class="graph-canvas"></div>

            <!-- Plain HTML badge, not a canvas label — a node with many
                 connections has edges radiating in every direction, and no
                 amount of in-canvas label styling reliably stays legible
                 through all of them. This always renders on top. -->
            <div class="graph-focus-badge" *ngIf="focusedNode() as node">
              <span class="dot" [style.background]="colorForType(node.type)"></span>
              {{ node.name }}
            </div>

            <div class="zoom-controls">
              <button type="button" (click)="zoomIn()" title="Zoom in" aria-label="Zoom in">+</button>
              <button type="button" (click)="zoomOut()" title="Zoom out" aria-label="Zoom out">−</button>
            </div>
          </div>

          <aside class="playground-sidebar">
            <section class="panel-section ask-section">
              <label class="panel-label">Ask the graph</label>
              <div class="ask-input-row">
                <input
                  type="text"
                  [ngModel]="question()"
                  (ngModelChange)="question.set($event)"
                  placeholder="e.g. What does iCode integrate with?"
                  [disabled]="askBusy()"
                  (keydown.enter)="askGraph()"
                />
                <button type="button" (click)="askGraph()" [disabled]="askBusy() || !question().trim()">
                  {{ askBusy() ? '…' : 'Ask' }}
                </button>
              </div>

              <div class="ask-result" *ngIf="askStatus() !== 'idle'">
                <div class="ask-status">
                  <span *ngIf="askStatus() === 'searching'">Searching the graph…</span>
                  <span *ngIf="askStatus() === 'answering' && !askAnswer()">Thinking…</span>
                  <span *ngIf="askStatus() === 'answering' && askAnswer()">Answering…</span>
                  <span *ngIf="askStatus() === 'done'">Done</span>
                  <span *ngIf="askStatus() === 'error'">Error</span>
                  <button class="clear-btn" type="button" (click)="clearAsk()" title="Clear">✕</button>
                </div>
                <div class="local-preview-note" *ngIf="askIsLocalPreview()">
                  Local preview only — no AI answer backend is connected yet.
                </div>
                <div class="ask-error" *ngIf="askError()">{{ askError() }}</div>
                <div class="ask-answer" *ngIf="askAnswer()">{{ askAnswer() }}</div>
              </div>
            </section>

            <section class="panel-section search-section">
              <label class="panel-label">Focus a node</label>
              <div class="search-wrap">
                <input
                  type="text"
                  [ngModel]="searchQuery()"
                  (ngModelChange)="onSearchInput($event)"
                  (focus)="searchResults().length && searchOpen.set(true)"
                  (blur)="closeSearchResults()"
                  placeholder="Search by name…"
                />
                <ul class="search-results" *ngIf="searchOpen() && searchResults().length">
                  <li
                    *ngFor="let result of searchResults()"
                    (mousedown)="selectSearchResult(result)"
                  >
                    <span class="dot" [style.background]="colorForType(result.type)"></span>
                    <span class="name">{{ result.name }}</span>
                    <span class="type">{{ result.type || 'Unknown' }}</span>
                  </li>
                </ul>
              </div>

              <div class="focus-inline" *ngIf="focusedNode() as node">
                <div class="focus-header">
                  <span class="dot" [style.background]="colorForType(node.type)"></span>
                  <div class="focus-title-group">
                    <div class="focus-name">{{ node.name }}</div>
                    <div class="focus-type">{{ node.type }}</div>
                  </div>
                  <button class="clear-btn" type="button" (click)="clearFocus()" title="Clear focus">✕</button>
                </div>

                <div class="connection-group" *ngIf="outgoingConnections().length">
                  <h4>Outgoing ({{ outgoingConnections().length }})</h4>
                  <ul class="connection-list">
                    <li *ngFor="let c of outgoingConnections()" (click)="focusNode(c.targetId)">
                      <span class="rel">{{ c.relationship }}</span>
                      <span class="target">{{ c.targetName }}</span>
                    </li>
                  </ul>
                </div>

                <div class="connection-group" *ngIf="incomingConnections().length">
                  <h4>Incoming ({{ incomingConnections().length }})</h4>
                  <ul class="connection-list">
                    <li *ngFor="let c of incomingConnections()" (click)="focusNode(c.sourceId)">
                      <span class="target">{{ c.sourceName }}</span>
                      <span class="rel">{{ c.relationship }}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            <section class="panel-section filter-section">
              <div class="filter-bar-header">
                <label class="panel-label">Entity types</label>
                <div class="filter-actions">
                  <button type="button" (click)="selectAllTypes()">All</button>
                  <button type="button" (click)="selectNoTypes()">None</button>
                </div>
              </div>
              <div class="type-chip-row">
                <button
                  type="button"
                  class="type-chip"
                  *ngFor="let entry of typeCounts()"
                  [class.active]="activeTypes().has(entry[0])"
                  [style.--chip-color]="colorForType(entry[0])"
                  (click)="toggleType(entry[0])"
                >
                  <span class="chip-dot"></span>
                  <span class="chip-label">{{ entry[0] }}</span>
                  <span class="chip-count">{{ entry[1] }}</span>
                </button>
              </div>
            </section>
          </aside>
        </div>
      </div>
    </div>
  </div>
</div>
